import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { compare } from 'fast-json-patch'
import { addMinutes, isBefore } from 'date-fns'

import { useBlockConflicts } from '../../../../../hooks/block/useBlockConflicts'
import { useCreateBlock } from '../../../../../hooks/block/useCreateBlock'
import { useUpdateBlock } from '../../../../../hooks/block/useUpdateBlock'

import { BlockForm } from '../blockForm/BlockForm'

import { useApp } from '../../../../../contexts/AppContext'
import { useConfirm } from '../../../../../contexts/ConfirmContext'
import { BannerKind } from '../../../../../types/contexts/AppContext'
import {
  BlockModel,
  CourtModel,
} from '../../../../../types/court/court'
import {
  ConfirmLayout,
} from '../../../../../types/confirm/confirm'
import {
  displayDate,
  displayLocalDateTimeHourMinute,
} from '../../../../../utils/dateUtils'

const BLOCKS_PATH = '/m/blocks'

type BlockEditProps = {
  courts: CourtModel[]
  initialValues: BlockModel
  isEditMode: boolean
  blockId?: number
}

export const BlockEdit = ({
  courts,
  initialValues,
  isEditMode,
  blockId,
}: BlockEditProps) => {
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const { confirm } = useConfirm()
  const { t, i18n } = useTranslation(['blocks', 'bookings', 'exceptions'])

  const {
    createBlock,
    loading: createLoading,
    error: createError,
    success: createSuccess,
    resetState: resetCreateState,
  } = useCreateBlock()

  const {
    updateBlock,
    loading: updateLoading,
    error: updateError,
    success: updateSuccess,
    resetState: resetUpdateState,
  } = useUpdateBlock()

  const {
    getBlockConflicts,
    loading: conflictsLoading,
    error: conflictsError,
    resetState: resetConflictsState,
  } = useBlockConflicts()

  const loading = createLoading || updateLoading || conflictsLoading

  useEffect(() => {
    const error = createError || updateError || conflictsError
    const success = createSuccess || updateSuccess

    if (error) {
      showBanner({
        kind: BannerKind.ERR,
        msg: error,
      })
    }

    if (success) {
      showBanner({
        kind: BannerKind.OK,
        msg: success,
      })
      navigate(BLOCKS_PATH, { replace: true })
    }

    resetState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conflictsError, createError, createSuccess, updateError, updateSuccess])

  const resetState = () => {
    resetCreateState()
    resetUpdateState()
    resetConflictsState()
  }

  const saveBlock = async (request: BlockModel, cancelConflicts = false) => {
    if (!isEditMode) {
      return await createBlock(request, cancelConflicts)
    }

    if (blockId === undefined) {
      return undefined
    }

    const patch = compare(initialValues, request)

    if (patch.length === 0) {
      return initialValues
    }

    return await updateBlock(request.courtId, blockId, patch, cancelConflicts)
  }

  const handleSubmit = async (request: BlockModel) => {
    if (loading) {
      return
    }

    const conflicts = await getBlockConflicts(request)

    if (!conflicts) {
      return
    }

    if (conflicts.length === 0) {
      await saveBlock(request)
      return
    }

    const blockedCourtIds = request.courtIds?.length ? request.courtIds : [request.courtId]
    const blockedCourtNames = courts
      .filter(court => court.id !== undefined && blockedCourtIds.includes(court.id))
      .map(court => court.name)
    const blockStart = new Date(request.startTime)
    const blockEnd = new Date(request.endTime)
    const isDirectConflict = (bookingStartTime: string, bookingEndTime: string | undefined, courtName: string) => {
      if (!blockedCourtNames.includes(courtName)) {
        return false
      }

      const bookingStart = new Date(bookingStartTime)
      const bookingEnd = bookingEndTime ? new Date(bookingEndTime) : addMinutes(bookingStart, 60)

      return (
        isBefore(bookingStart, blockEnd) && isBefore(blockStart, bookingEnd)
      )
    }
    const groupedConflicts = conflicts.map(slot => ({
      slot,
      direct: isDirectConflict(slot.bookingStartTime, slot.bookingEndTime, slot.courtName),
    }))

    confirm({
      layout: ConfirmLayout.DETAILS,
      title: t('blocks:blocks.block-conflicts.title', {
        count: conflicts.length,
      }),
      subtitle: t('blocks:blocks.block-conflicts.subtitle'),
      description: t('blocks:blocks.block-conflicts.description', {
        reason: t(`blocks:blocks.reasons.${request.reason}`),
      }),
      confirmLabel: t('blocks:blocks.block-conflicts.confirm'),
      backLabel: t('blocks:blocks.actions.cancel'),
      returnTo: BLOCKS_PATH,
      payload: request,
      itemCards: groupedConflicts.map(({ slot, direct }) => ({
        id: slot.id,
        title: slot.courtName,
        subtitle: t(
          direct
            ? 'blocks:blocks.block-conflicts.direct'
            : 'blocks:blocks.block-conflicts.related'
        ),
        details: [
          {
            label: t('bookings:booking-confirmation.date-label'),
            value: displayDate(slot.bookingStartTime, i18n.language),
          },
          {
            label: t('bookings:booking-confirmation.start-time-label'),
            value: displayLocalDateTimeHourMinute(slot.bookingStartTime),
          },
          {
            label: t(
              'bookings:booking.all-booking-list.table-header.booked-by'
            ),
            value: slot.bookedBy,
          },
        ],
      })),
      onConfirm: async (_, __, payload) => {
        // The bookings are cancelled with the block's own reason, which every customer receives by email.
        const savedBlock = await saveBlock(payload as BlockModel, true)

        if (!savedBlock) {
          throw new Error(t('exceptions:unknown'))
        }

        showBanner({
          kind: BannerKind.OK,
          msg: t(
            isEditMode
              ? 'blocks:blocks.successfully-updated'
              : 'blocks:blocks.successfully-created'
          ),
        })
      },
    })
  }

  return (
    <BlockForm
      key={blockId ?? `${initialValues.courtId}-${initialValues.startTime}`}
      courts={courts}
      initialValues={initialValues}
      isEditMode={isEditMode}
      loading={loading}
      onSubmit={handleSubmit}
    />
  )
}
